import React from 'react';
import { useTranslation } from 'react-i18next';

import { General } from 'icons/untitledui';
import colours from 'theme/atoms/colours';
import * as DropdownStyled from 'theme/components/Dropdown.style';
import * as SelectionStyled from 'theme/components/SelectionDropdown.style';
import { EntityTag } from 'shiftNotes/components/EntityTag/_index';
import { EntityTagColors } from 'shiftNotes/components/EntityTag/EntityTag';
import * as Styled from '../../styles/FilterGroupingControl.style';
import { Urgency } from './_types';

interface UrgencyFilterMenuProps {
  selected: Urgency | null;
  onSelect: (value: Urgency | null) => void;
  topOffset?: number;
}

const URGENCY_ORDER: Urgency[] = [
  'urgent',
  'needs_attention',
  'routine',
  'informational',
];

const URGENCY_COLORS: Record<Urgency, EntityTagColors> = {
  urgent: {
    bg: colours.primary[100],
    border: colours.primary[200],
    text: colours.primary[800],
  },
  needs_attention: {
    bg: '#FFF3CF',
    border: colours.warning[100],
    text: colours.warning[800],
  },
  routine: {
    bg: '#D9F2FF',
    border: colours.teal[100],
    text: colours.teal[800],
  },
  informational: {
    bg: colours.neutral[100],
    border: colours.neutral[200],
    text: colours.neutral[700],
  },
};

export default function UrgencyFilterMenu({
  selected,
  onSelect,
  topOffset,
}: UrgencyFilterMenuProps): JSX.Element {
  const { t } = useTranslation('pageEvents');

  return (
    <Styled.PopoverCard $zIndex={1400} $topOffset={topOffset} $leftOffset={-40}>
      <Styled.List>
        <DropdownStyled.SectionHeader>
          {t('filterGrouping.urgency.title')}
        </DropdownStyled.SectionHeader>
        {URGENCY_ORDER.map((urgency) => {
          const isSelected = selected === urgency;
          return (
            <SelectionStyled.Option
              key={urgency}
              type="button"
              $selected={isSelected}
              $compact
              onClick={() => onSelect(isSelected ? null : urgency)}
              role="menuitemradio"
              aria-checked={isSelected}
            >
              <Styled.PillWrapper>
                <EntityTag
                  extractionClass="urgency"
                  category={t(`filterGrouping.urgency.options.${urgency}`)}
                  colors={URGENCY_COLORS[urgency]}
                />
              </Styled.PillWrapper>
              <SelectionStyled.OptionCheck $selected={isSelected}>
                {isSelected && <General.Check size={16} />}
              </SelectionStyled.OptionCheck>
            </SelectionStyled.Option>
          );
        })}
      </Styled.List>
    </Styled.PopoverCard>
  );
}
